// react
import { useForm } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

// api
import { type ReadUnitDto } from '@/api/generated/recalc/endpoints/v1/units';

// shared components
import { SubmitButton } from '@/components/ui/button';

// feature-hooks
import { useCreateAlarmConfiguration } from '@/features/alarm-hub/hooks/useCreateAlarmConfiguration';

// relative
import { normalizeDataStreamOptions } from '../dataStreamUtils';
import { buildConfigurationZodSchema } from '../schemaUtils';
import { AlarmConfigurationDataStreamFields } from '../shared/AlarmConfigurationDataStreamFields';
import { AlarmConfigurationDetailsFields } from '../shared/AlarmConfigurationDetailsFields';
import {
  toAlarmConfigurationAssociations,
  toAlarmConfigurationFormValues,
  toPostAlarmConfigurationDto,
} from '../shared/alarmConfigurationForm.mappers';
import { createAlarmConfigurationFormSchema } from '../shared/alarmConfigurationForm.schema';
import { RHFJsonSchemaFields } from '../shared/RHFJsonSchemaFields';
import type { JsonSchemaNode, StandaloneFormProps } from '../types';

// react-hook-form
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const DEFAULT_UOT_SUBTYPE = 'ThresholdExceeded';

export function CreateUsageOverTimeConfiguration({
  accountId,
  configurationSchema,
  units,
}: StandaloneFormProps & { units: ReadUnitDto[] }) {
  const { t } = useTranslation(['forms', 'validation']);

  const { createAlarmConfiguration, isCreating } =
    useCreateAlarmConfiguration();

  let parsedSchema: JsonSchemaNode | null = null;
  try {
    parsedSchema = configurationSchema
      ? (JSON.parse(configurationSchema) as JsonSchemaNode)
      : null;
  } catch {
    parsedSchema = null;
  }

  const typedUnits = units as ReadUnitDto[];
  const dataStreamOptions = normalizeDataStreamOptions(typedUnits);

  let configurationZodSchema: z.ZodType<
    Record<string, unknown>,
    Record<string, unknown>
  >;
  let isConfigurationSchemaInvalid = false;
  try {
    configurationZodSchema = buildConfigurationZodSchema(parsedSchema);
  } catch {
    configurationZodSchema = z.record(z.string(), z.unknown());
    isConfigurationSchemaInvalid = true;
  }

  const schema = createAlarmConfigurationFormSchema(t, configurationZodSchema);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toAlarmConfigurationFormValues(
      undefined,
      parsedSchema,
      DEFAULT_UOT_SUBTYPE,
      [],
    ),
    mode: 'onTouched',
  });

  const onSubmit = handleSubmit(async (values) => {
    const associations = toAlarmConfigurationAssociations(
      typedUnits,
      dataStreamOptions,
      values.dataStreamKeys,
    );

    const createPayload = toPostAlarmConfigurationDto(
      values,
      accountId,
      'UsageOverTime',
      associations,
    );

    await createAlarmConfiguration(createPayload);
  });

  if (!parsedSchema) {
    return (
      <Alert severity="warning">
        {t('alarmConfigurationForm.schemaNotSupported')}
      </Alert>
    );
  }

  if (isConfigurationSchemaInvalid) {
    return (
      <Alert severity="error">
        {t('alarmConfigurationForm.schemaInvalid')}
      </Alert>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <AlarmConfigurationDetailsFields control={control} />

              <AlarmConfigurationDataStreamFields
                control={control}
                dataStreamOptions={dataStreamOptions}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">
                {t('alarmConfigurationForm.settingsTitle', {
                  ns: 'forms',
                  defaultValue: 'Configuration Settings',
                })}
              </Typography>

              <RHFJsonSchemaFields
                schema={parsedSchema}
                control={control}
                alarmType="UsageOverTime"
              />
            </Stack>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end">
          <SubmitButton disabled={isCreating || !isValid}>
            {isCreating && <CircularProgress size={16} sx={{ mr: 1 }} />}
            {t('common.save')}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
